import { ReplayOutlined } from '@mui/icons-material';
import { CircularProgress, IconButton, Tooltip } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { RecentFailureItem } from '@/api/generatedApi';
import { useRemediateFailures } from '@/hooks/api/failures/useRemediateFailures';
import { useToast } from '@/hooks/useToast';
import { useTenants } from '@/services/tenants/useTenants';
import { getApiError } from '@/types/apiError';
import { failureLink } from '../failureLinks';
import { RecreateReportLink } from './RecreateReportLink';
import { describeRemediationOutcome, isRemediableKind, REMEDIATION_COPY } from './remediationCopy';
import { useCanRemediate } from './useCanRemediate';

export const RowRemediationButton = ({ failure }: { failure: RecentFailureItem }) => {
  const canRemediate = useCanRemediate(failure.kind);
  const { showToast } = useToast();
  const { mutate, isPending } = useRemediateFailures();
  const navigate = useNavigate();
  const { getPath } = useTenants();

  if (!canRemediate) return;
  if (!isRemediableKind(failure.kind)) return <RecreateReportLink testId='row-recreate-report' />;

  const kind = failure.kind;
  const copy = REMEDIATION_COPY[kind];
  const label = `${copy.verbImperative} ${failure.name}`;

  const remediate = () =>
    mutate(
      { kind, ids: [failure.id] },
      {
        onSuccess: result =>
          showToast({
            ...describeRemediationOutcome(result, copy),
            action:
              result.queued.length > 0
                ? {
                    label: 'View',
                    onClick: () => {
                      void navigate(getPath(failureLink(kind, failure.id)));
                    },
                  }
                : undefined,
          }),
        onError: error =>
          showToast({ type: 'error', message: getApiError(error)?.message ?? `Could not ${copy.verb}.` }),
      }
    );

  return (
    <Tooltip title={copy.verbImperative}>
      <span>
        <IconButton
          size='small'
          color='secondary'
          onClick={remediate}
          disabled={isPending}
          aria-label={label}
          data-testid='row-remediation'
        >
          {isPending ? <CircularProgress size={16} /> : <ReplayOutlined fontSize='small' />}
        </IconButton>
      </span>
    </Tooltip>
  );
};
